// Angles and loads read from the controls (HTML currentFn/currentFv/currentAlphaElem1…).

import { isPanelMat, isSteelMat, pInt, type EngineCtx } from "./ctx";

export const D2R = Math.PI / 180;
export const sin = (d: number) => Math.sin(d * D2R);
export const cos = (d: number) => Math.cos(d * D2R);

export function fmtN(x: number | null | undefined, dec?: number): string {
  return typeof x === "number" && Number.isFinite(x) ? x.toFixed(dec == null ? 2 : dec) : "—";
}

// Angle in degrees brought to (-180°, 180°], same sine/cosine.
export function wrapAngle180(deg: number): number {
  let a = deg % 360;
  if (a <= -180) a += 360;
  if (a > 180) a -= 360;
  return a;
}

// Acute angle (0°–90°) between a direction and an axis, regardless of the sense (170° from the axis ≈ 10°).
export function foldAcuteAngle(deg: number): number {
  const v = Math.abs(deg) % 180;
  return v > 90 ? 180 - v : v;
}

// Fn/Fv from the inputs, default Fn=1/Fv=0 when empty (→ alpha1 = 0°).
export function currentFn(ctx: EngineCtx): number {
  const v = ctx.v.forceFn;
  return Number.isFinite(v) ? v : 1;
}
export function currentFv(ctx: EngineCtx): number {
  const v = ctx.v.forceFv;
  return Number.isFinite(v) ? v : 0;
}
// Fa: axial withdrawal force along the screw axis (positive = pulls the screw out), default 0.
export function currentFa(ctx: EngineCtx): number {
  const v = ctx.v.forceFa;
  return Number.isFinite(v) ? v : 0;
}

// Force/grain angle of element 1: the force is decomposed in element 1's local frame (Fn axial, Fv shear) →
// alpha1 = atan2(Fv, Fn). Element 2: alpha2 = theta − alpha1 in (-180°, 180°].
export function currentAlphaElem1(ctx: EngineCtx): number {
  return (Math.atan2(currentFv(ctx), currentFn(ctx)) * 180) / Math.PI;
}
export function currentAlphaElem2(ctx: EngineCtx): number {
  const theta = ctx.v.thetaNum;
  return wrapAngle180(theta - currentAlphaElem1(ctx));
}

// Grain orientation φ (0°/90°) relative to the element axis: meaningless for steel/panels, and not considered for
// CLT (the crossed layup is already in the CLT model) → 0.
export function currentFi1(ctx: EngineCtx): number {
  if (isSteelMat(ctx.v.materiale1) || isPanelMat(ctx.v.materiale1) || ctx.v.mat1Tipo === "Pannelli CLT") return 0;
  return pInt(ctx.v.fi1) || 0;
}
export function currentFi2(ctx: EngineCtx): number {
  if (ctx.v.mat2Tipo === "Pannelli CLT") return 0;
  return pInt(ctx.v.fi2) || 0;
}
